import styled from "@emotion/styled";
import { Button, Card, Col, Row, Tabs, Typography, message } from "antd";
import { goBack } from "assets/icons/index";
import ParcelCreditCard from "components/ManageParcel/ParcelShared/ParcelCreditCard";
import {
  ParcelCardWrapperTable,
  ParcelHeadWrapper,
  ParcelRightIconWrapper,
  ParcelWrapperAll,
} from "components/ManageParcel/manageParcelLayout";
import { useFactoryCreditTransactions } from "hooks/useFactoryCreditTransactions";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFactoryCreditTransactions } from "services/factoryCreditApi";
import CreditTransactionSearchForm from "./CreditTransactionSearchForm";
import CreditTransactionTable from "./CreditTransactionTable";
import CreditTopupHistorySection from "./CreditTopupHistorySection";
import { EMPTY_CREDIT_SEARCH, CreditSearchDraft } from "./creditTransactionOptions";
import { exportCreditTransactionsExcel } from "./exportCreditTransactionsExcel";

/**
 * ตาราง + ค้นหา + export ของแท็บ "ประวัติการใช้เครดิต" (ทุกประเภท เลือก type/system_type เองได้)
 * แท็บ "จัดการเครดิต" ย้ายไปใช้ CreditTopupHistorySection แยกต่างหากแล้ว (2026-09-02 — มุมมองคนละแบบ
 * เน้นการสั่งซื้อ ไม่ใช่ ledger ทั่วไป ดู docstring ของไฟล์นั้น) ไม่ใช้ fixedType/showTopUpButton แล้ว
 * 2026-09-07: ปุ่ม "เติมเครดิต" ย้ายมาไว้ที่นี่ด้วย (ปกติอยู่แค่ CreditTopupHistorySection) เพราะแท็บ
 * "จัดการเครดิต" ถูกซ่อนไปแล้ว (ดู SHOW_TOPUP_TAB) — ไม่งั้นไม่มีทางเข้าเติมเครดิตได้เลย
 */
const CreditHistorySection: FC = () => {
  const navigate = useNavigate();
  const [applied, setApplied] = useState<CreditSearchDraft>(EMPTY_CREDIT_SEARCH);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [exporting, setExporting] = useState(false);

  const queryParams = useMemo(
    () => ({
      page,
      limit,
      date_from: applied.date_from || undefined,
      date_to: applied.date_to || undefined,
      system_type: applied.system_type || undefined,
      type: applied.type || undefined,
      keyword: applied.keyword || undefined,
    }),
    [page, limit, applied]
  );

  const { data, isFetching } = useFactoryCreditTransactions(queryParams);

  const handleSearch = (next: CreditSearchDraft) => {
    setApplied(next);
    setPage(1);
  };

  const handleClear = () => {
    setApplied(EMPTY_CREDIT_SEARCH);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await getFactoryCreditTransactions({
        ...queryParams,
        page: 1,
        export: true,
      });
      if (!res.data?.length) {
        message.info("ไม่มีรายการตามตัวกรองนี้");
        return;
      }
      if (res.truncated) {
        message.warning("ส่งออกได้สูงสุด 5,000 รายการตามตัวกรองนี้ — รายการที่เกินจะไม่ถูกรวม");
      }
      exportCreditTransactionsExcel(res.data);
    } catch (e) {
      console.error(e);
      message.error("ส่งออก Excel ไม่สำเร็จ");
    } finally {
      setExporting(false);
    }
  };

  return (
    // 2026-09-01: เดิมลืมห่อ ParcelWrapperAll (พื้นเทา #efefef + padding) ตอนแยกเป็นแท็บ — ตาราง/search
    // เลยพื้นขาวกลืนกับการ์ด ไม่เหมือนแท็บของ "คำสั่งซื้อ" ที่แต่ละแท็บห่อ WrapperAll ของตัวเองเสมอ
    // (ดู OrderShipped/index.tsx) ใส่กลับให้ตรงกัน
    <ParcelWrapperAll>
      <ParcelHeadWrapper>
        <CreditTransactionSearchForm applied={applied} onSearch={handleSearch} onClear={handleClear} />
        <ParcelRightIconWrapper>
          <Button type="default" onClick={handleExport} loading={exporting}>
            Export Excel
          </Button>
          <Button type="primary" style={{ marginLeft: 8 }} onClick={() => navigate("/manage-credit/topup")}>
            เติมเครดิต
          </Button>
        </ParcelRightIconWrapper>
      </ParcelHeadWrapper>

      <ParcelCardWrapperTable>
        <CreditTransactionTable
          data={data?.data ?? []}
          loading={isFetching}
          total={data?.total ?? 0}
          page={page}
          limit={limit}
          onPageChange={(nextPage, nextSize) => {
            setPage(nextPage);
            setLimit(nextSize);
          }}
        />
      </ParcelCardWrapperTable>
    </ParcelWrapperAll>
  );
};

/**
 * 2026-09-07: ตามที่ขอ — ซ่อนแท็บ "จัดการเครดิต" ไปก่อน (ไม่ลบโค้ด CreditTopupHistorySection ออก
 * เผื่อกลับมาเปิดใช้ใหม่ทีหลัง) เหลือแท็บเดียวคือ "ประวัติการใช้เครดิต" ให้เป็นค่าเริ่มต้น/แท็บเดียว
 * ที่แสดง — สลับกลับมาแสดงทั้ง 2 แท็บได้แค่ตั้ง SHOW_TOPUP_TAB = true
 */
const SHOW_TOPUP_TAB = false;

/**
 * 2026-09-01: ปรับให้เหมือนหน้า "คำสั่งซื้อ" (OrderManage/index.tsx) ตามที่ขอ — การ์ดเดียว มี Tabs
 * ในตัว (renderTabBar แบบเดียวกัน: ซ้าย = ปุ่มย้อนกลับ+หัวข้อ+แท็บ, ขวา = การ์ดเครดิต) แทนที่ header
 * แบบเดิม (ManageParcelPageHeader + การ์ดเครดิตเต็มความกว้างเหนือตาราง)
 *   - แท็บ "จัดการเครดิต"      → CreditTopupHistorySection — datatable มุมมองการสั่งซื้อ (2026-09-02:
 *     เลขที่ทำรายการ/วันที่/package/ผู้ทำรายการ/ช่องทาง/สถานะ) + ปุ่ม "เติมเครดิต" (ซ่อนอยู่ ดู SHOW_TOPUP_TAB)
 *   - แท็บ "ประวัติการใช้เครดิต" → ตารางเดิมทั้งหมด (ทุกประเภท, ตัวกรองครบเหมือนเดิม)
 */
const ManageCreditList: FC = () => {
  const navigate = useNavigate();
  const [tabKey, setTabKey] = useState(SHOW_TOPUP_TAB ? "topup" : "history");

  const handleBack = () => {
    if (window.history.state && typeof window.history.state.idx === "number" && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }
    navigate("/dashboard");
  };

  const items = [
    ...(SHOW_TOPUP_TAB
      ? [
          {
            key: "topup",
            label: "จัดการเครดิต",
            children: <CreditTopupHistorySection />,
          },
        ]
      : []),
    {
      key: "history",
      label: "ประวัติการใช้เครดิต",
      children: <CreditHistorySection />,
    },
  ];

  return (
    <Card bordered={false} className="criclebox h-full tabs-mobile">
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col sm={24} md={24}>
          <Tabs
            activeKey={tabKey}
            style={{ height: "100%" }}
            items={items}
            onChange={setTabKey}
            renderTabBar={(props, DefaultTabBar) => (
              <TopSplit>
                <LeftPane>
                  <HeaderTabs>
                    <IconWrapper onClick={handleBack}>
                      <SpanIcon>{goBack}</SpanIcon>
                    </IconWrapper>
                    <Typography.Title level={5} style={{ margin: 0, flex: 1, textAlign: "center" }}>
                      จัดการเครดิตและพอยต์
                    </Typography.Title>
                    <HeaderBalanceSpacer />
                  </HeaderTabs>
                  {/* เหลือแท็บเดียว — ไม่โชว์แถบแท็บเลย (ซ่อนไว้เฉยๆ ไม่ลบ ดู SHOW_TOPUP_TAB) */}
                  {items.length > 1 && <DefaultTabBar {...props} />}
                </LeftPane>
                <RightPane>
                  {/* ไม่ตีเส้น — เหมือนการ์ดเครดิตมุมขวาบนของ "คำสั่งซื้อ" (OrderCodCreditCard.tsx) */}
                  <ParcelCreditCard bordered={false} />
                </RightPane>
              </TopSplit>
            )}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default ManageCreditList;

/** ซ้าย = หัวข้อ+แท็บ (ยืดเต็มที่เหลือ) / ขวา = การ์ดเครดิตกว้างตามเนื้อหา — โครงเดียวกับ OrderManage/index.tsx */
const TopSplit = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const LeftPane = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  .ant-tabs-nav {
    margin-top: 12px;
    margin-bottom: 0;
    padding-left: 16px;
  }
`;

const RightPane = styled.div`
  flex: 0 0 auto;
  width: max-content;
  padding: 8px 10px 0 0;
`;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px 8px 10px;
  flex-direction: row;
`;

const HeaderBalanceSpacer = styled.div`
  width: 40px;
  height: 30px;
  flex-shrink: 0;
  visibility: hidden;
  pointer-events: none;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
